import React, { useEffect, useReducer } from 'react';
import isEqual from 'react-fast-compare';
import type { AxiosRequestConfig } from 'axios';

import type { FetchHookReturnType } from './types';
import { dataFetchReducer, fetchData } from './utils';

export interface UseDataFetcherOptions {
  shouldFetch?: boolean;
  shouldReportAxiosError?: boolean;
  /**
   * Whether a reported error also opens the modal error dialog. Set false for a fetch the user did not
   * ask for — a probe on mount whose failure the UI already expresses — so the failure is still
   * reported without a modal in front of someone who came to this screen for something else.
   */
  shouldOpenErrorDialog?: boolean;
  /** Milliseconds between automatic background refetches. Omit to fetch on mount and reload only. */
  refetchInterval?: number;
}

type ResolvedUseDataFetcherOptions = Required<Omit<UseDataFetcherOptions, 'refetchInterval'>> &
  Pick<UseDataFetcherOptions, 'refetchInterval'>;

const defaultFetchOptions: ResolvedUseDataFetcherOptions = {
  shouldFetch: true,
  shouldReportAxiosError: true,
  shouldOpenErrorDialog: true,
};

function getOptions(optionsOrShouldFetch?: boolean | UseDataFetcherOptions): ResolvedUseDataFetcherOptions {
  if (typeof optionsOrShouldFetch === 'boolean') {
    return { ...defaultFetchOptions, shouldFetch: optionsOrShouldFetch };
  } else if (typeof optionsOrShouldFetch === 'object') {
    return {
      ...defaultFetchOptions,
      ...(optionsOrShouldFetch || {}),
    };
  }
  return defaultFetchOptions;
}

function useDataFetcher<T = unknown>(
  url: string | null,
  config?: AxiosRequestConfig | null,
  optionsOrShouldFetch?: boolean | UseDataFetcherOptions
): FetchHookReturnType<T> {
  const { shouldFetch, shouldReportAxiosError, shouldOpenErrorDialog, refetchInterval } =
    getOptions(optionsOrShouldFetch);

  const lastParamsRef = React.useRef<[string | null, AxiosRequestConfig]>([url, config || {}]);
  const [currParams, setCurrParams] = React.useState<[string | null, AxiosRequestConfig]>([url, config || {}]);

  const reloadData = React.useCallback(async () => {
    const [currUrl, currConfig] = currParams;
    const action = await fetchData<T>(currUrl, currConfig, shouldReportAxiosError, shouldOpenErrorDialog);
    if (action) {
      dispatch(action);
      if (action.type === 'FETCH_SUCCESS') {
        return action.payload;
      }
    }
  }, [currParams, shouldReportAxiosError, shouldOpenErrorDialog]);

  const [state, dispatch] = useReducer(dataFetchReducer<T>, {
    isLoading: shouldFetch,
    isError: false,
    data: undefined,
  });

  useEffect(() => {
    const [currUrl, currConfig] = currParams;
    let didCancel = false;
    const callFetchData = async () => {
      dispatch({ type: 'FETCH_INIT' });
      const action = await fetchData<T>(currUrl, currConfig, shouldReportAxiosError, shouldOpenErrorDialog);
      if (!didCancel) {
        dispatch(action);
      }
    };
    if (shouldFetch) {
      callFetchData();
    }
    return () => {
      didCancel = true;
    };
  }, [currParams, shouldFetch, shouldReportAxiosError, shouldOpenErrorDialog]);

  useEffect(() => {
    if (!shouldFetch || !refetchInterval) {
      return;
    }
    const [currUrl, currConfig] = currParams;
    // A null url makes fetchData return FETCH_INIT, which would defeat the no-loading-state
    // guarantee below on every tick, so there is nothing to poll for.
    if (!currUrl) {
      return;
    }
    let didCancel = false;
    // Chained setTimeout rather than setInterval: the next delay starts only once the previous
    // response has landed, so a request slower than the interval cannot pile up further requests
    // behind it (and cannot let an out-of-order response overwrite a newer one). The period is
    // therefore interval + request duration, which is the intended trade for a background poll.
    //
    // No FETCH_INIT: a background refetch must not drop consumers back into their loading state
    // every interval. Error reporting is off for the same reason — the timer fires unattended, so a
    // transient failure would toast repeatedly at a user who never asked for a refresh. The mount
    // fetch and explicit reloadData() keep the caller's shouldReportAxiosError.
    const poll = async () => {
      const action = await fetchData<T>(currUrl, currConfig, false);
      if (didCancel) {
        return;
      }
      dispatch(action);
      timeoutId = setTimeout(poll, refetchInterval);
    };
    let timeoutId = setTimeout(poll, refetchInterval);
    return () => {
      didCancel = true;
      clearTimeout(timeoutId);
    };
  }, [currParams, shouldFetch, refetchInterval]);

  React.useEffect(() => {
    if (lastParamsRef.current) {
      const [lastUrl, lastConfig] = lastParamsRef.current;
      if (lastUrl === url && isEqual(lastConfig, config || {})) {
        return;
      }
    }
    setCurrParams([url, config || {}]);
    lastParamsRef.current = [url, config || {}];
  }, [url, config]);

  return { ...state, reloadData };
}

export default useDataFetcher;
